import type { User } from '@supabase/supabase-js';
import { ServiceFout, type AuthService, type Gebruiker, type Rol } from '../types';
import { getSupabase, leesbareFout } from './supabaseClient';

/**
 * Inloggen met e-mailadres en wachtwoord.
 *
 * Accounts worden niet in de app aangemaakt: dat doet de beheerder in het
 * Supabase-dashboard (Authentication -> Users -> Add user). Zo kan niemand
 * zichzelf toegang geven tot de gegevens.
 *
 * Naam en rol komen uit de tabel `profielen`. De rol bepaalt of iemand
 * registreert (coach) of alleen meekijkt zonder persoonsgegevens (gemeente).
 * Die tabel is voor de app alleen-lezen; wijzigen gebeurt in het dashboard.
 */

interface ProfielRij {
  naam: string | null;
  rol: string | null;
}

/** Naam uit het e-mailadres, als terugval zolang er geen profiel is ingevuld. */
function naamUitEmail(email: string): string {
  return email.split('@')[0] || 'Onbekend';
}

/**
 * Laatst bekende profiel per gebruiker.
 *
 * Zonder netwerk mislukt het ophalen van naam en rol. Zou de app dan
 * terugvallen op de veiligste rol, dan wordt een coach midden in een gesprek
 * gedegradeerd tot meekijker en kan hij niets meer opslaan. Het profiel dat
 * eerder bij een geslaagde aanmelding is opgehaald, blijft daarom bewaard.
 */
const PROFIEL_SLEUTEL = 'afkoppelcoach.profiel';

function bewaarProfiel(gebruiker: Gebruiker): void {
  try {
    localStorage.setItem(`${PROFIEL_SLEUTEL}.${gebruiker.id}`, JSON.stringify(gebruiker));
  } catch {
    // Opslag vol of geblokkeerd; dan valt hij bij netwerkuitval alsnog terug.
  }
}

function bewaardProfiel(userId: string): Gebruiker | null {
  try {
    const rauw = localStorage.getItem(`${PROFIEL_SLEUTEL}.${userId}`);
    return rauw ? (JSON.parse(rauw) as Gebruiker) : null;
  } catch {
    return null;
  }
}

async function haalProfiel(user: User): Promise<Gebruiker> {
  const email = user.email ?? '';

  const { data, error } = await getSupabase()
    .from('profielen')
    .select('naam, rol')
    .eq('id', user.id)
    .maybeSingle();

  if (error || !data) {
    // Eerder opgehaald profiel wint van de terugval: dat is wat de beheerder
    // heeft ingesteld, en het is niet door de gebruiker te beïnvloeden.
    const eerder = bewaardProfiel(user.id);
    if (eerder) return eerder;

    // Niets bekend: de veiligste rol. Liever iemand die te weinig ziet dan
    // iemand die te veel ziet.
    return { id: user.id, email, naam: naamUitEmail(email), rol: 'gemeente' };
  }

  const profiel = data as ProfielRij;
  const gebruiker: Gebruiker = {
    id: user.id,
    email,
    naam: profiel.naam?.trim() || naamUitEmail(email),
    rol: (profiel.rol === 'coach' ? 'coach' : 'gemeente') as Rol,
  };

  bewaarProfiel(gebruiker);
  return gebruiker;
}

export const supabaseAuthService: AuthService = {
  async huidigeGebruiker(): Promise<Gebruiker | null> {
    const { data, error } = await getSupabase().auth.getSession();
    if (error) throw new ServiceFout(leesbareFout(error), error);
    return data.session?.user ? haalProfiel(data.session.user) : null;
  },

  async inloggen(email: string, wachtwoord: string): Promise<Gebruiker> {
    const { data, error } = await getSupabase().auth.signInWithPassword({
      email: email.trim(),
      password: wachtwoord,
    });

    if (error) throw new ServiceFout(leesbareFout(error), error);
    if (!data.user) throw new ServiceFout('Inloggen lukte, maar er kwam geen gebruiker terug.');

    return haalProfiel(data.user);
  },

  async uitloggen(): Promise<void> {
    const { error } = await getSupabase().auth.signOut();
    if (error) throw new ServiceFout(leesbareFout(error), error);
  },

  abonneer(bijWijziging: (gebruiker: Gebruiker | null) => void): () => void {
    const { data } = getSupabase().auth.onAuthStateChange((gebeurtenis, sessie) => {
      if (sessie?.user) {
        void haalProfiel(sessie.user).then(bijWijziging);
        return;
      }

      // Zonder sessie: alleen uitloggen als de gebruiker daar zelf om vroeg.
      //
      // Valt het netwerk weg terwijl het token toe is aan vernieuwen, dan komt
      // hier ook een lege sessie langs. De coach staat dan midden in een
      // gesprek en zou opeens op het inlogscherm belanden, terwijl de sessie
      // lokaal nog prima is. Bij herstel van de verbinding wordt het token
      // alsnog vernieuwd.
      if (gebeurtenis === 'SIGNED_OUT' && navigator.onLine) {
        bijWijziging(null);
      }
    });
    return () => data.subscription.unsubscribe();
  },
};
